import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Btn, Field, Input, Select, Empty } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin", client_ids: [], active: true, mobile: "", designation: "" });
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); }, []);

  const openNew = () => { setForm({ name: "", email: "", password: "", role: "admin", client_ids: [], active: true, mobile: "", designation: "" }); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setEditId(u.id); setOpen(true); };
  const openDetail = async (u) => { const { data } = await api.get(`/users/${u.id}/detail`); setDetail(data); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleClient = (id) => {
    const has = form.client_ids.includes(id);
    setForm({ ...form, client_ids: has ? form.client_ids.filter((x) => x !== id) : [...form.client_ids, id] });
  };

  const save = async () => {
    try {
      if (editId) await api.put(`/users/${editId}`, form);
      else await api.post("/users", form);
      toast.success("User saved"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cnames = (ids) => (ids || []).map((id) => clients.find((c) => c.id === id)?.code || "?").join(", ");

  return (
    <div>
      <PageHeader title="Users" subtitle="Super Admins and Admins with assigned clients"
        actions={<Btn onClick={openNew} data-testid="add-user-btn"><Plus size={16} /> Add User</Btn>} />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {users.length ? (
            <table className="nk-table" data-testid="users-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Assigned Clients</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="font-medium">{u.name || "—"}</td><td>{u.email}</td>
                    <td className="capitalize">{(u.role || "").replace("_", " ")}</td>
                    <td className="text-slate-500">{u.role === "super_admin" ? "All" : (cnames(u.client_ids) || "—")}</td>
                    <td><StatusBadge status={u.active ? "active" : "inactive"} /></td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <Btn variant="ghost" onClick={() => openDetail(u)} data-testid={`view-user-${u.email}`}><Eye size={14} /></Btn>
                        <Btn variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil size={14} /></Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty title="No users" />}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><Input value={form.name} onChange={set("name")} data-testid="user-name" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} disabled={!!editId} data-testid="user-email" /></Field>
            <Field label="Mobile"><Input value={form.mobile || ""} onChange={set("mobile")} data-testid="user-mobile" /></Field>
            <Field label="Designation / Job Title"><Input value={form.designation || ""} onChange={set("designation")} data-testid="user-designation" /></Field>
            <Field label={editId ? "New Password (optional)" : "Password"}><Input type="password" value={form.password} onChange={set("password")} data-testid="user-password" /></Field>
            <Field label="Role">
              <Select value={form.role} onChange={set("role")} data-testid="user-role"><option value="admin">Admin</option><option value="super_admin">Super Admin</option></Select>
            </Field>
            <Field label="Status"><Select value={form.active ? "true" : "false"} onChange={(e) => setForm({ ...form, active: e.target.value === "true" })}><option value="true">Active</option><option value="false">Inactive</option></Select></Field>
          </div>
          {form.role === "admin" && (
            <div>
              <div className="nk-overline mb-2">Assigned Clients</div>
              <div className="flex flex-wrap gap-2">
                {clients.map((c) => (
                  <button key={c.id} onClick={() => toggleClient(c.id)} data-testid={`assign-client-${c.code}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${form.client_ids.includes(c.id) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn onClick={save} data-testid="save-user-btn">Save User</Btn>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>User Profile</DialogTitle></DialogHeader>
          {detail && (
            <div data-testid="user-detail">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-4">
                <div><span className="text-slate-400">Name:</span> <b>{detail.user.name || "—"}</b></div>
                <div><span className="text-slate-400">Email:</span> {detail.user.email}</div>
                <div><span className="text-slate-400">Mobile:</span> {detail.user.mobile || "—"}</div>
                <div><span className="text-slate-400">Designation:</span> {detail.user.designation || "—"}</div>
                <div><span className="text-slate-400">Role:</span> {(detail.user.role || "").replace("_", " ")}</div>
                <div><span className="text-slate-400">Status:</span> {detail.user.active ? "Active" : "Inactive"}</div>
                <div><span className="text-slate-400">Last Login:</span> {(detail.user.last_login || "—").slice(0, 16).replace("T", " ")}</div>
                <div><span className="text-slate-400">Created:</span> {(detail.user.created_at || "—").slice(0, 10)}</div>
              </div>
              <div className="nk-overline mb-1">Assigned Clients</div>
              <div className="text-sm text-slate-600 mb-4">{detail.user.role === "super_admin" ? "All clients" : (detail.assigned_clients.join(", ") || "—")}</div>
              <div className="nk-overline mb-1">Recent Activity</div>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {detail.activity.length ? detail.activity.map((a) => (
                  <div key={a.id} className="text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1 flex justify-between">
                    <span className="capitalize">{(a.action || "").replace(/_/g, " ")}</span>
                    <span className="text-slate-400">{(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                )) : <div className="text-sm text-slate-400">No recent activity.</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
